import React, { Fragment, ReactNode, forwardRef, useMemo } from "react";

import {
  portableTextDefaultBlockRenderers,
  portableTextDefaultMarkRenderers,
} from "./renderers";
import {
  isPortableTextBlock,
  isPortableTextContent,
  isPortableTextObjectNode,
  isPortableTextSpan,
  PortableTextBlock,
  PortableTextComponents,
  PortableTextContent,
  PortableTextDefaultBlockType,
  PortableTextDefaultMark,
  PortableTextListItemBlock,
  PortableTextObjectNode,
  PortableTextSpan,
  PortableTextUnknownTypeHandler,
} from "./types";
import { Space } from "../LayoutTokens";
import { Stack } from "../Stack";

export interface IPortableTextProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "content"> {
  /** The message body: the node list an API returns under `content`. */
  content: PortableTextContent;
  /**
   * Application-injected renderers: catalog marks and objects, optional
   * default-block overrides, and the unknown-type hook. See README.md.
   */
  components?: PortableTextComponents;
  /** Vertical rhythm between top-level nodes. */
  space?: Space;
}

interface IWalkContext {
  components?: PortableTextComponents;
  report: PortableTextUnknownTypeHandler;
}

const defaultUnknownTypeHandler: PortableTextUnknownTypeHandler = (
  kind,
  type
) => {
  console.warn(`PortableText: no renderer for ${kind} type "${type}".`);
};

/**
 * A span's marks wrap its text inside out. An unknown mark is reported and
 * skipped — the text stays, because dropping a word would break the sentence.
 */
const renderSpan = (span: PortableTextSpan, ctx: IWalkContext): ReactNode =>
  (span.marks ?? []).reduce<ReactNode>((children, mark) => {
    const type = typeof mark === "string" ? mark : mark.type;
    const Renderer =
      ctx.components?.marks?.[type] ??
      portableTextDefaultMarkRenderers[type as PortableTextDefaultMark];

    if (!Renderer) {
      ctx.report("mark", type);
      return children;
    }

    return (
      <Renderer mark={typeof mark === "string" ? undefined : mark}>
        {children}
      </Renderer>
    );
  }, span.text);

/**
 * Traversal is structural, so the walker keeps no list of block types of its
 * own: a `children` array holds spans or nested blocks, a nested `content`
 * holds a node list, and anything else has no children to render.
 */
const renderBlockChildren = (
  block: PortableTextBlock | PortableTextListItemBlock,
  ctx: IWalkContext
): ReactNode => {
  const { children, content } = block as {
    children?: unknown;
    content?: unknown;
  };

  if (Array.isArray(children)) {
    return children.map((child, index) => (
      <Fragment key={index}>
        {isPortableTextSpan(child)
          ? renderSpan(child, ctx)
          : isPortableTextBlock(child)
          ? renderBlock(child, ctx)
          : null}
      </Fragment>
    ));
  }

  return isPortableTextContent(content) ? renderNodes(content, ctx) : undefined;
};

const renderBlock = (
  block: PortableTextBlock | PortableTextListItemBlock,
  ctx: IWalkContext
): ReactNode => {
  const type = block.type as PortableTextDefaultBlockType;
  const Renderer =
    ctx.components?.blocks?.[type] ?? portableTextDefaultBlockRenderers[type];

  if (!Renderer) {
    ctx.report("block", block.type);
    return null;
  }

  // Blocks that carry spans outside `children` (table cells) render them
  // through this engine-bound helper, so mark injection works there too.
  const renderSpans = (spans: PortableTextSpan[]): ReactNode =>
    spans.map((span, index) => (
      <Fragment key={index}>{renderSpan(span, ctx)}</Fragment>
    ));

  return (
    <Renderer block={block as PortableTextBlock} renderSpans={renderSpans}>
      {renderBlockChildren(block, ctx)}
    </Renderer>
  );
};

const renderObject = (
  node: PortableTextObjectNode,
  ctx: IWalkContext
): ReactNode => {
  const Renderer = ctx.components?.objects?.[node._type];

  if (!Renderer) {
    ctx.report("object", node._type);
    return null;
  }

  return (
    <Renderer node={node}>
      {isPortableTextContent(node.content)
        ? renderNodes(node.content, ctx)
        : undefined}
    </Renderer>
  );
};

const renderNodes = (
  nodes: PortableTextContent,
  ctx: IWalkContext
): ReactNode =>
  nodes.map((node, index) => (
    <Fragment key={index}>
      {isPortableTextObjectNode(node)
        ? renderObject(node, ctx)
        : renderBlock(node, ctx)}
    </Fragment>
  ));

/**
 * The Portable Text engine: walks a {@link PortableTextContent} envelope and
 * renders it with the default renderers for the universal set, plus whatever
 * the application injects through `components`. The engine itself is hollow —
 * it knows no product element; an unknown block or object renders nothing and
 * is reported once per type through `onUnknownType` (console.warn when the
 * hook is absent).
 */
export const PortableText = forwardRef<HTMLDivElement, IPortableTextProps>(
  ({ content, components, space = "md", ...rest }, ref) => {
    // Memoized so unknown types are reported once per type for a given
    // content/components pair, not again on every parent re-render.
    const nodes = useMemo(() => {
      const reported = new Set<string>();
      const report: PortableTextUnknownTypeHandler = (kind, type) => {
        const key = `${kind}:${type}`;
        if (reported.has(key)) return;
        reported.add(key);
        (components?.onUnknownType ?? defaultUnknownTypeHandler)(kind, type);
      };

      return renderNodes(content, { components, report });
    }, [content, components]);

    return (
      <Stack ref={ref} space={space} {...rest}>
        {nodes}
      </Stack>
    );
  }
);

PortableText.displayName = "PortableText";
